import { useCallback, useEffect, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { GiftPanel } from "@/components/GiftPanel";
import { RevenuePanel } from "@/components/RevenuePanel";
import { FunnelPanel } from "@/components/FunnelPanel";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/PageHeader";
import { api, fmtDate, fmtEur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Kpi = ({ label, value, id, accent }) => (
  <div className={`rounded-2xl border p-5 ${accent ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white"}`} data-testid={`kpi-${id}`}>
    <p className={`text-xs uppercase tracking-[0.15em] ${accent ? "text-blue-200" : "text-slate-500"}`}>{label}</p>
    <p className="mt-2 text-3xl font-display font-semibold">{value}</p>
  </div>
);

const proLabel = (u) => {
  if (u.pro_source === "stripe") return "Pro · pagante";
  if (u.pro_source === "gift") return u.gift_pro_until === "forever" ? "Pro · regalo per sempre" : `Pro · regalo fino al ${fmtDate(u.gift_pro_until)}`;
  return "Free";
};

export default function Admin() {
  const [s, setS] = useState(null);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [giftEmail, setGiftEmail] = useState("");
  const loadUsers = useCallback(() => {
    api.get("/admin/users", { params: q ? { q } : {} }).then(({ data }) => setUsers(data));
  }, [q]);
  const loadStats = () => api.get("/admin/stats").then(({ data }) => setS(data));
  useEffect(() => {
    loadStats();
  }, []);
  useEffect(() => {
    const t = setTimeout(loadUsers, 250);
    return () => clearTimeout(t);
  }, [loadUsers]);
  const { refresh } = useAuth();
  const refreshAll = () => {
    loadUsers();
    loadStats();
    refresh();
  };
  const revoke = async (u) => {
    if (!window.confirm(`Revocare il Pro regalato a ${u.email}?`)) return;
    await api.delete(`/admin/gift/${u.user_id}`);
    refreshAll();
  };
  const pickGift = (u) => {
    setGiftEmail(u.email);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  if (!s) return <div className="h-40 rounded-3xl bg-slate-100 animate-pulse" />;

  return (
    <div data-testid="admin-page">
      <PageHeader eyebrow="Admin" title="Iscrizioni e conversioni" desc="Numeri in tempo reale di Burocrazy." />
      <div className="mb-6"><GiftPanel email={giftEmail} setEmail={setGiftEmail} onDone={refreshAll} /></div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <Kpi label="Utenti totali" value={s.total_users} id="total-users" accent />
        <Kpi label="Nuovi 7 giorni" value={s.new_7d} id="new-7d" />
        <Kpi label="Nuovi 30 giorni" value={s.new_30d} id="new-30d" />
        <Kpi label="Utenti Pro (totale)" value={s.pro_users + s.gifted_users} id="pro-users" />
        <Kpi label="Pro paganti" value={s.pro_users} id="pro-paying" />
        <Kpi label="Conversione Free→Pro" value={`${s.conversion_rate}%`} id="conversion-rate" />
        <Kpi label="Checkout avviati" value={s.checkouts_started} id="checkouts" />
        <Kpi label="Conversione checkout" value={`${s.checkout_conversion}%`} id="checkout-conversion" />
        <Kpi label="MRR stimato" value={fmtEur(s.mrr)} id="mrr" />
        <Kpi label="Incassato totale" value={fmtEur(s.revenue_total)} id="revenue" />
        <Kpi label="Documenti" value={s.documents} id="documents" />
        <Kpi label="Analisi AI" value={s.analyses} id="analyses" />
        <Kpi label="Pro in regalo" value={s.gifted_users} id="gifted-users" />
        <Kpi label="Iscritti da referral" value={s.referrals} id="referrals" />
        <Kpi label="Referral diventati Pro" value={s.referrals_paid} id="referrals-paid" />
      </div>
      <div className="mt-6 rounded-3xl border bg-white p-6" data-testid="admin-chart">
        <h3 className="font-medium mb-4">Ultimi 30 giorni</h3>
        <div className="h-72">
          <ResponsiveContainer>
            <AreaChart data={s.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={12} />
              <YAxis allowDecimals={false} fontSize={12} />
              <Tooltip />
              <Area type="monotone" dataKey="signups" name="Iscrizioni" stroke="#1E3A8A" fill="#1E3A8A" fillOpacity={0.15} />
              <Area type="monotone" dataKey="conversions" name="Conversioni" stroke="#E07A5F" fill="#E07A5F" fillOpacity={0.25} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      <FunnelPanel />
      <RevenuePanel />
      <div className="mt-6 rounded-3xl border bg-white p-6 overflow-x-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <h3 className="font-medium">Utenti</h3>
          <div className="relative md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca email o nome" className="pl-9" data-testid="admin-users-search" />
          </div>
        </div>
        <Table data-testid="admin-users-table">
          <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Email</TableHead><TableHead>Piano</TableHead><TableHead>Inviti</TableHead><TableHead>Iscritto</TableHead><TableHead>Ultimo accesso</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}>
                <TableCell>{u.name}{u.role === "admin" && <span className="ml-2 text-[10px] rounded-full bg-slate-100 px-2 py-0.5">admin</span>}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell data-testid={`admin-user-plan-${u.user_id}`}><span className={`text-xs rounded-full px-2.5 py-1 ${u.plan === "pro" ? "bg-[#1E3A8A] text-white" : "bg-slate-100"}`}>{proLabel(u)}</span></TableCell>
                <TableCell>{u.referrals}</TableCell>
                <TableCell>{fmtDate(u.created_at)}</TableCell>
                <TableCell>{fmtDate(u.last_login)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => pickGift(u)} data-testid={`admin-gift-btn-${u.user_id}`}>Regala Pro</Button>
                  {u.pro_source === "gift" && <Button size="sm" variant="ghost" className="rounded-full text-red-600 ml-1" onClick={() => revoke(u)} data-testid={`admin-revoke-btn-${u.user_id}`}>Revoca</Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
